import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Controlled native `<dialog>` (the fee-disclosure pattern, made reusable):
 * `open` drives showModal/close, Esc (native cancel) and backdrop clicks call
 * `onClose`, focus containment comes from the platform. Point `labelledBy` at
 * a heading id inside `children`. Style overrides (e.g. the nav drawer's
 * full-height left sheet) merge through `className`.
 */
export function Dialog({
  open,
  onClose,
  labelledBy,
  children,
  className,
}: {
  open: boolean
  onClose: () => void
  labelledBy: string
  children: ReactNode
  className?: string
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    else if (!open && dialog.open) dialog.close()
  }, [open])

  function handleBackdropClick(event: MouseEvent<HTMLDialogElement>) {
    // Clicks inside land on children; only the backdrop targets the dialog itself.
    if (event.target === event.currentTarget) onClose()
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events -- backdrop dismiss; the keyboard path is the dialog's native Esc/cancel
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={handleBackdropClick}
      aria-labelledby={labelledBy}
      className={cn(
        'm-auto w-full max-w-md rounded-lg border border-rule bg-card p-6 shadow-overlay backdrop:bg-ink/30',
        className,
      )}
    >
      {children}
    </dialog>
  )
}
